// Lightweight analytics helper
// Safe to import on server — only calls window methods when available
export function trackEvent(name, params = {}) {
    // When imported on the server nothing should happen.
    if (typeof window === 'undefined') return false;

    // Only print debug logs outside of production. This keeps production
    // console quiet and allows test assertions to rely on the boolean return.
    const isDev = process.env.NODE_ENV !== 'production';

    try {
        // gtag (Google Analytics 4)
        if (typeof window.gtag === 'function') {
            window.gtag('event', name, params);
            return true;
        }

        // dataLayer (classic gtag setup)
        if (Array.isArray(window.dataLayer)) {
            window.dataLayer.push({ event: name, ...params });
            return true;
        }

        // Fallback for other setups — only debug log in non-production
        if (isDev) {
            // eslint-disable-next-line no-console
            console.debug('trackEvent (debug)', name, params);
        }

        return false;
    } catch (e) {
        // swallow errors to avoid breaking the app; only log in dev
        if (isDev) {
            // eslint-disable-next-line no-console
            console.debug('trackEvent error', e);
        }
        return false;
    }
}

/**
 * Track a user navigating to a new section (e.g., when scrolling into view)
 */
export function trackSectionView(section, params = {}) {
    return trackEvent('section_view', {
        section,
        page_path:
            typeof window !== 'undefined'
                ? window.location.pathname
                : undefined,
        ...params,
    });
}

/**
 * Track a CTA (call-to-action) button or link click
 */
export function trackCTAClick(ctaText, params = {}) {
    return trackEvent('cta_click', {
        cta_text: ctaText,
        page_path:
            typeof window !== 'undefined'
                ? window.location.pathname
                : undefined,
        ...params,
    });
}

/**
 * Track internal navigation link clicks
 */
export function trackNavClick(linkText, linkHref, params = {}) {
    return trackEvent('nav_link_click', {
        link_text: linkText,
        link_href: linkHref,
        page_path:
            typeof window !== 'undefined'
                ? window.location.pathname
                : undefined,
        ...params,
    });
}

/**
 * Track inquiry funnel steps
 */
export function trackInquiryFunnelStep(step, stepNumber, params = {}) {
    return trackEvent('inquiry_funnel_step', {
        step,
        step_number: stepNumber,
        page_path:
            typeof window !== 'undefined'
                ? window.location.pathname
                : undefined,
        ...params,
    });
}

/**
 * Track form field errors
 */
export function trackFormError(formName, errorField, errorType, params = {}) {
    return trackEvent('form_error', {
        form_name: formName,
        error_field: errorField,
        error_type: errorType,
        ...params,
    });
}

/**
 * Track resource views (case studies, guides, FAQs, etc.)
 * Reserved for future implementation on resources pages
 * @future Integrate with /resources pages for case studies, guides, FAQ tracking
 */
export function trackResourceView(resourceType, resourceId, params = {}) {
    return trackEvent('resource_view', {
        resource_type: resourceType,
        resource_id: resourceId,
        page_path:
            typeof window !== 'undefined'
                ? window.location.pathname
                : undefined,
        ...params,
    });
}

/**
 * Track brochure/download interactions
 * Reserved for future implementation on resource/product download flows
 * @future Integrate with brochure download endpoint and product documentation
 */
export function trackDownloadStart(downloadType, params = {}) {
    return trackEvent('brochure_download_start', {
        download_type: downloadType,
        page_path:
            typeof window !== 'undefined'
                ? window.location.pathname
                : undefined,
        ...params,
    });
}

export function trackDownloadComplete(downloadType, params = {}) {
    return trackEvent('brochure_download_complete', {
        download_type: downloadType,
        page_path:
            typeof window !== 'undefined'
                ? window.location.pathname
                : undefined,
        ...params,
    });
}

/**
 * Track time spent on a section or page
 */
export function trackTimeSpent(section, timeSeconds, params = {}) {
    return trackEvent('section_time_spent', {
        section,
        time_seconds: Math.round(timeSeconds),
        page_path:
            typeof window !== 'undefined'
                ? window.location.pathname
                : undefined,
        ...params,
    });
}

/**
 * Track upload failures
 */
export function trackUploadFailed(
    fileName,
    errorReason,
    fileSizeMb,
    params = {},
) {
    return trackEvent('upload_failed', {
        file_name: fileName,
        error_reason: errorReason,
        file_size_mb: fileSizeMb,
        ...params,
    });
}

/**
 * Track submission/API errors
 */
export function trackSubmissionError(submissionType, errorType, params = {}) {
    return trackEvent('submission_error', {
        submission_type: submissionType,
        error_type: errorType,
        page_path:
            typeof window !== 'undefined'
                ? window.location.pathname
                : undefined,
        ...params,
    });
}

export default trackEvent;
